import React from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { getContrastTextColorRGB } from "utils/getContrastTextColor";
import { useModalContext } from "components/modal";
import ChangeColorModal from "./ChangeColorModal";
import { IStatus } from "./interface";
import { useSnackbarContext } from "components/snackbar";
import { useTranslation } from 'react-i18next';
import { useAuthContext } from "contexts/auth";

const Index = () => {
  const { t } = useTranslation();
  const tBase = "views.admin.status-colors.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { showSnackbar } = useSnackbarContext();
  const { company_id: companyId } = useAuthContext();

  const { setModal } = useModalContext();
  const queryClient = useQueryClient();

  const { data, isLoading} = useQuery("statusColors", async () => {
    const res = await axios.get( 
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/status/custom`, 
    );
    return res.data;
  });

  const { colorScheme, statuses } = data || {};

  const addCustomColor = async (id: number, color: string, type: string) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/status/${id}`,
        { color, type }
      );
      queryClient.invalidateQueries("statusColors");
      showSnackbar(tr("Color updated successfully"));
    } catch (e) {
      showSnackbar(tr("Error updating color"));
      console.error(e);
    }
  }

  const noStatJob = ["Converted to Job"];
  const noStatProj = ["Won", "Lost"];
  const noStatQuote2 = ["Denied"];

  const groupByType = (statuses as IStatus[])?.reduce((acc: { [key: string]: IStatus[] }, status: IStatus) => {

    if (status.type === "job") return acc; // no job
    if ((companyId !== 1 && companyId !== 13) && noStatJob.includes(status.label)) return acc; // no converted to job except for MS && WeatherGuard
    // if ((companyId !== 1 && companyId !== 13) && noStatQuote1.includes(status.label)) return acc; // no converted to job except for MS && WeatherGuard
    if ((companyId !== 1 && companyId !== 13) && (status.id == 22 && status.label === "Approved")) return acc; // no converted to job except for MS && WeatherGuard
    if (companyId !== 1 && noStatQuote2.includes(status.label)) return acc; // no converted to job except for MS 
    if (companyId !== 1 && status.type === "project" && noStatProj.includes(status.label)) return acc; // no won or lost for projects

    if (!acc[status.type]) acc[status.type] = [];
    acc[status.type].push(status);
    return acc;
  }, {});

  const closeModal = () => {
    setModal();
  };

  if (isLoading) return <div className="text-center text-lg">{tr("Loading")}...</div>;

  const getTagStyle = (type: string, color: string) => {
    let style;

    if (type === "appointment") {
      style = {
        backgroundColor: `rgb(${color})`,
        color: getContrastTextColorRGB(color)
      }
    } else {
      if (color === "255,255,255") {
        style = {
          backgroundColor: "transparent",
          color: "black",
          border: "1px solid #ccc",
          fontWeight: 800,
        };
      } else {
        style = {
          backgroundColor: `rgba(${color}, 0.15)`,
          color: `rgb(${color})`,
          fontWeight: 800,
        }
      }
    }

    return style;
  };

  return (
    <div>
      <div className="mb-6">
        <h2>{tr("Customize Status Colors")}</h2>
        <p>{tr("Please select a status to change its color.")}</p>
      </div>
      {Object.entries(groupByType || {}).map(([type, statuses]) => (
        <div key={type} className="mb-6 border border-gray-200 rounded-lg p-4 shadow-md space-y-2">
          <h2 className="text-xl font-semibold capitalize mb-4">
            {type === "service_plan" ? tr("Service Plan") : tr(type)}
          </h2>
          <div className="flex flex-wrap gap-2">
            {statuses.map((status) => {
              const { id, label, color_v2, color, custom_color } = status;
              const currentColor = custom_color
                ? custom_color
                : colorScheme === "legacy"
                ? color
                : color_v2;

              return (
                <div key={id}>
                  <button 
                    className={`
                      text-white px-3 py-1 rounded-md text-sm font-medium hover:scale-[1.1] border transition-transform
                      ${currentColor === "255,255,255" ? "border border-gray-300" : ""}
                    `} 
                    style={getTagStyle(type, currentColor)}
                    onClick={() => 
                      setModal({
                        label: tr("Change Status Color"),
                        component: 
                          <ChangeColorModal 
                            status={status}
                            currentColor={currentColor}
                            colorScheme={colorScheme}
                            addCustomColor={addCustomColor} 
                            closeModal={closeModal}
                            getTagStyle={getTagStyle}
                          />
                      }) 
                    }
                  >
                    {tr(label)}
                  </button>
                  
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default Index;